<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { ChevronLeft, ChevronRight, X, Download, Volume2, VolumeX } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatFileSize } from '@/config/media'

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  resources: {
    type: Array,
    required: true
  },
  initialIndex: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['update:open', 'delete'])

const currentIndex = ref(props.initialIndex)
const videoRef = ref(null)
const audioRef = ref(null)
const isMuted = ref(false)
const isPlaying = ref(false)

const currentResource = computed(() => {
  return props.resources[currentIndex.value] || null
})

const isImage = computed(() => currentResource.value?.file_type === 'image')
const isVideo = computed(() => currentResource.value?.file_type === 'video')
const isAudio = computed(() => currentResource.value?.file_type === 'audio')

const canGoPrevious = computed(() => currentIndex.value > 0)
const canGoNext = computed(() => currentIndex.value < props.resources.length - 1)

const handlePrevious = () => {
  if (canGoPrevious.value) {
    currentIndex.value--
  }
}

const handleNext = () => {
  if (canGoNext.value) {
    currentIndex.value++
  }
}

const handleClose = () => {
  emit('update:open', false)
}

const handleDownload = () => {
  if (!currentResource.value) return

  const link = document.createElement('a')
  link.href = currentResource.value.url
  link.download = currentResource.value.filename
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const toggleMute = () => {
  isMuted.value = !isMuted.value
  if (videoRef.value) {
    videoRef.value.muted = isMuted.value
  }
  if (audioRef.value) {
    audioRef.value.muted = isMuted.value
  }
}

const handleKeyDown = (e) => {
  if (!props.open) return

  switch (e.key) {
    case 'ArrowLeft':
      handlePrevious()
      break
    case 'ArrowRight':
      handleNext()
      break
    case 'Escape':
      handleClose()
      break
  }
}

// Reset index when resources change
watch(() => props.resources, () => {
  currentIndex.value = 0
})

// Reset playing state when resource changes
watch(currentResource, () => {
  isPlaying.value = false
})

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-6xl h-[90vh] p-0 overflow-hidden">
      <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <div class="flex items-center gap-4">
            <DialogTitle class="text-lg font-semibold">
              {{ currentResource?.filename || 'Media Viewer' }}
            </DialogTitle>
            <span v-if="currentResource" class="text-sm text-gray-500">
              {{ formatFileSize(currentResource.file_size) }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <Button
              @click="handleDownload"
              variant="ghost"
              size="sm"
            >
              <Download class="w-4 h-4 mr-2" />
              Download
            </Button>

            <Button
              v-if="isVideo || isAudio"
              @click="toggleMute"
              variant="ghost"
              size="sm"
            >
              <component :is="isMuted ? VolumeX : Volume2" class="w-4 h-4" />
            </Button>

            <Button
              @click="handleClose"
              variant="ghost"
              size="sm"
            >
              <X class="w-4 h-4" />
            </Button>
          </div>
        </div>

        <!-- Main content area -->
        <div class="flex-1 relative flex items-center justify-center overflow-hidden">
          <!-- Image viewer -->
          <img
            v-if="isImage"
            :src="currentResource.url"
            :alt="currentResource.filename"
            class="max-w-full max-h-full object-contain"
          />

          <!-- Video player -->
          <video
            v-else-if="isVideo"
            ref="videoRef"
            :src="currentResource.url"
            controls
            :muted="isMuted"
            class="max-w-full max-h-full"
          >
            Your browser does not support the video tag.
          </video>

          <!-- Audio player -->
          <div v-else-if="isAudio" class="w-full max-w-2xl px-8">
            <div class="bg-white/10 backdrop-blur rounded-lg p-8">
              <!-- Audio visualization placeholder -->
              <div class="mb-6 h-32 bg-white/5 rounded-lg flex items-center justify-center">
                <div class="flex items-end gap-1 h-20">
                  <div
                    v-for="i in 20"
                    :key="i"
                    class="w-2 bg-primary animate-pulse"
                    :style="{
                      height: Math.random() * 100 + '%',
                      animationDelay: i * 50 + 'ms'
                    }"
                  ></div>
                </div>
              </div>

              <!-- Audio controls -->
              <audio
                ref="audioRef"
                :src="currentResource.url"
                controls
                :muted="isMuted"
                class="w-full"
              >
                Your browser does not support the audio element.
              </audio>

              <div class="mt-4 text-center text-white">
                <p class="text-lg font-medium">{{ currentResource.filename }}</p>
                <p class="text-sm opacity-70">{{ formatFileSize(currentResource.file_size) }}</p>
              </div>
            </div>
          </div>

          <!-- Navigation buttons -->
          <Button
            v-if="props.resources.length > 1 && canGoPrevious"
            @click="handlePrevious"
            variant="ghost"
            size="icon"
            class="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white"
          >
            <ChevronLeft class="w-6 h-6" />
          </Button>

          <Button
            v-if="props.resources.length > 1 && canGoNext"
            @click="handleNext"
            variant="ghost"
            size="icon"
            class="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white"
          >
            <ChevronRight class="w-6 h-6" />
          </Button>
        </div>

        <!-- Footer with thumbnails -->
        <div v-if="props.resources.length > 1" class="p-4 border-t bg-gray-50">
          <div class="flex gap-2 justify-center overflow-x-auto">
            <button
              v-for="(resource, index) in props.resources"
              :key="resource.id"
              @click="currentIndex = index"
              class="flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all"
              :class="{
                'border-primary': index === currentIndex,
                'border-transparent opacity-60 hover:opacity-100': index !== currentIndex
              }"
            >
              <img
                v-if="resource.file_type === 'image'"
                :src="resource.thumbnail_url || resource.url"
                :alt="resource.filename"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-gray-200 flex items-center justify-center"
              >
                <span class="text-xs text-gray-500">
                  {{ resource.file_type.toUpperCase() }}
                </span>
              </div>
            </button>
          </div>

          <!-- Counter -->
          <div class="text-center mt-2 text-sm text-gray-600">
            {{ currentIndex + 1 }} / {{ props.resources.length }}
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
@keyframes pulse {
  0%, 100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
